// src/components/books/TableBookEditor.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Trash2, 
  Copy, 
  BookOpen,
  Edit3, 
  Settings, 
  HelpCircle,
  ChevronRight,
  X,
  Calculator,
  Eye,
  Code,
  Upload,
  Link,
  Image,
  Video,
  File,
  GripVertical,
  AlertCircle,
  Check,
  Move,
  Lock,
  Unlock,
  Award,  
  Target
} from 'lucide-react';
import { storage, functions } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { MathField } from '../math/MathField';
import { MathRenderer } from '../math/MathRenderer';
import { MathUtils } from '../math/MathUtils';
import { ConditionModal } from './modals/ConditionModal';
import { UserResponseTypeModal } from './modals/UserResponseTypeModal';
import { ActionModal } from './modals/ActionModal';
import { IntegrationModal } from './modals/IntegrationModal';
import { MediaModal } from './modals/MediaModal';
import { MessageConfigModal } from './modals/MessageConfigModal';

export const TableBookEditor = ({ messages, onMessagesChange, book, bookCategory }) => {
  const [activeModal, setActiveModal] = useState(null);
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [previewMode, setPreviewMode] = useState({});
  const [mathInitialized, setMathInitialized] = useState(false);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const textareaRefs = useRef({});

  // Initialize math libraries
  useEffect(() => {
    const initMath = async () => {
      await Promise.all([
        MathUtils.initializeMathJax(),
        MathUtils.initializeMathLive()
      ]);
      setMathInitialized(true);
    };
    initMath();
  }, []);

  // Validation functions
  const validateMessage = (message, index) => {
    const errors = {};
    
    if (!message.message || message.message.trim() === '') {
      errors.message = 'Message content is required';
    }
    
    if (message.userResponseType && !message.variable) {
      errors.variable = 'Variable name is required when response type is set';
    }
    
    if (message.userResponseType === 'select' || message.userResponseType === 'multiselect') {
      if (!message.integrationParameter || message.integrationParameter.trim() === '') {
        errors.integrationParameter = 'Options are required for select/multiselect types';
      }
    }
    
    return errors;
  };

  const validateAllMessages = () => {
    const errors = {};
    messages.forEach((message, index) => {
      const messageErrors = validateMessage(message, index);
      if (Object.keys(messageErrors).length > 0) {
        errors[index] = messageErrors;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleMessageConfigSave = (configData) => {
    if (editingMessageIndex !== null) {
      const updatedMessages = [...messages];
      updatedMessages[editingMessageIndex] = {
        ...updatedMessages[editingMessageIndex],
        metadata: configData.metadata,
        scoring: configData.scoring,
        assessment: configData.assessment,
        analytics: configData.analytics,
        // Update category if provided
        category: book?.category || 'education'
      };
      
      console.log('Message configuration updated:', updatedMessages[editingMessageIndex]);
      onMessagesChange(updatedMessages);
    }
    closeModal();
  };

  const updateMessage = (index, field, value) => {
    const updated = [...messages];
    updated[index] = { ...updated[index], [field]: value };
    onMessagesChange(updated);
    
    // Clear validation errors for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[index]) {
        delete newErrors[index][field];
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      return newErrors;
    });
  };

  const deleteMessage = (index) => {
    const updated = messages.filter((_, i) => i !== index);
    // Update order for remaining messages
    const reorderedMessages = updated.map((msg, i) => ({
      ...msg,
      order: i
    }));
    onMessagesChange(reorderedMessages);
  };

  const duplicateMessage = (index) => {
    const original = messages[index];
    const duplicate = {
      ...original,
      messageId: `msg_${Date.now()}`,
      order: messages.length
    };
    onMessagesChange([...messages, duplicate]);
  };

  const addMessage = () => {
    const newMessage = {
      // FIXED: Always generate proper message ID
      messageId: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      bookId: book?.id || null,
      message: '',
      section: '',
      type: 'text',
      userResponseType: '',
      variable: '',
      condition: '',
      action: '',
      integrationParameter: '',
      integrationType: '',
      mediaUrl: '',
      order: messages.length,
      status: 'active',
      // Add default enhanced fields
      category: book?.category || 'education',
      metadata: {
        topics: [],
        subtopics: [],
        tags: [],
        learningObjectives: [],
        knowledgeLevel: 'understand',
        difficulty: 'medium',
        estimatedTime: 5,
        prerequisites: []
      },
      scoring: {
        enabled: false,
        type: 'question',
        maxPoints: 10
      },
      assessment: {
        isGraded: false,
        autoGrade: true,
        showCorrectAnswer: 'after_submission'
      },
      analytics: {
        trackingEnabled: true,
        trackTime: true,
        trackAttempts: true
      }
    };
    
    onMessagesChange([...messages, newMessage]);
  };

  // Enhanced Drag and Drop Functions - Vertical Only
  const handleDragStart = (e, index, isDragHandle = false) => {
    // Only allow dragging from the drag handle or when drag mode is enabled
    if (!dragEnabled && !isDragHandle) {
      e.preventDefault();
      return;
    }

    // Check if this is a proper drag event with dataTransfer
    if (!e.dataTransfer) {
      // If called from mousedown (like drag handle), we need to enable draggable
      // and let the browser handle the actual dragstart event
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    
    // Add custom drag image for better UX
    const dragPreview = document.createElement('div');
    dragPreview.className = 'drag-preview';
    dragPreview.textContent = `Moving: ${messages[index].section || `Message ${index + 1}`}`;
    dragPreview.style.cssText = `
      position: absolute;
      top: -1000px;
      background: #3b82f6;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);
    
    setTimeout(() => document.body.removeChild(dragPreview), 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only allow vertical movement
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const dropIndex = e.clientY < midpoint ? index : index + 1;
    
    setDragOverIndex(dropIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const updatedMessages = [...messages];
    const draggedMessage = updatedMessages[draggedIndex];
    
    // Remove dragged message
    updatedMessages.splice(draggedIndex, 1);
    
    // Insert at new position
    const insertIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    updatedMessages.splice(insertIndex, 0, draggedMessage);
    
    // Update order values
    const reorderedMessages = updatedMessages.map((msg, index) => ({
      ...msg,
      order: index
    }));
    
    onMessagesChange(reorderedMessages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const togglePreview = (messageIndex) => {
    setPreviewMode(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };

  const openModal = (modalType, messageIndex, field = null) => {
    setActiveModal(modalType);
    setEditingMessageIndex(messageIndex);
    setEditingField(field);
  };

  const closeModal = () => {
    setActiveModal(null);
    setEditingMessageIndex(null);
    setEditingField(null);
  };

  const handleModalSave = (value) => {
    if (editingMessageIndex !== null && editingField) {
      updateMessage(editingMessageIndex, editingField, value);
    }
    closeModal();
  };

  const handleMediaSave = (mediaData) => {
    if (editingMessageIndex !== null) {
      updateMessage(editingMessageIndex, 'mediaUrl', mediaData.url);
    }
    closeModal();
  };

  const hasComplexContent = (text) => {
    return text && (
      text.includes('\\(') || 
      text.includes('\\[') || 
      text.includes('$$') ||
      text.includes('**') ||
      text.includes('*') ||
      text.includes('#') ||
      text.includes('```') ||
      text.includes('`')
    );
  };

  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 600);
      textarea.style.height = `${newHeight}px`;
    }
  };

  const getMediaIcon = (mediaUrl) => {
    if (!mediaUrl) return null;
    
    const url = mediaUrl.toLowerCase();
    if (url.includes('image') || url.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      return <Image className="h-4 w-4" />;
    }
    if (url.includes('video') || url.match(/\.(mp4|avi|mov|wmv)$/)) {
      return <Video className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const validateAndProceed = () => {
    const isValid = validateAllMessages();
    if (!isValid) {
      alert('Please fix validation errors before proceeding');
      return false;
    }
    return true;
  };

  return (
    <div className="space-y-4 max-w-5xl mx-auto bg-white editor-container" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
      {/* Header with Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Book Content</h3>
          {Object.keys(validationErrors).length > 0 && (
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{Object.keys(validationErrors).length} validation error(s)</span>
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Drag Mode Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDragEnabled(!dragEnabled)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                dragEnabled 
                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}
              title={dragEnabled ? 'Disable drag mode' : 'Enable drag mode'}
            >
              {dragEnabled ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              {dragEnabled ? 'Drag: ON' : 'Drag: OFF'}
            </button>
            <span className="text-xs text-gray-500">
              {dragEnabled ? 'Click cards to drag' : 'Safe editing mode'}
            </span>
          </div>

          <div className="h-6 w-px bg-gray-300"></div>

          <button
            onClick={validateAndProceed}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <Check className="h-4 w-4" />
            Validate All
          </button>
          
          <button
            onClick={addMessage}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="h-4 w-4" />
            Add Content
          </button>
        </div>
      </div>

      {/* Card-based Layout for All Devices */}
      <div className="space-y-4">
        {messages.map((message, index) => (
          <MessageCard
            key={message.messageId}
            message={message}
            index={index}
            onUpdate={updateMessage}
            onDelete={deleteMessage}
            onDuplicate={duplicateMessage}
            onTogglePreview={() => togglePreview(index)}
            previewMode={previewMode[index]}
            hasComplexContent={hasComplexContent(message.message)}
            mathInitialized={mathInitialized}
            openModal={openModal}
            validationErrors={validationErrors[index]}
            onOpenModal={openModal}
            bookCategory={book?.category || 'education'}
            dragEnabled={dragEnabled}
            dragHandlers={{
              onDragStart: (e) => handleDragStart(e, index),
              onDragOver: (e) => handleDragOver(e, index),
              onDragEnd: handleDragEnd,
              onDrop: (e) => handleDrop(e, index),
              isDragged: draggedIndex === index,
              isDragOver: dragOverIndex === index
            }}
          />
        ))}
        
        {messages.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No content yet</h3>
            <p className="text-gray-600 mb-4">Start by adding your first message or section</p>
            <button
              onClick={addMessage}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto"
            >
              <Plus className="h-4 w-4" />
              Add First Message
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {activeModal === 'condition' && (
        <ConditionModal
          isOpen={true}
          onClose={closeModal}
          onSave={handleModalSave}
          currentValue={messages[editingMessageIndex]?.condition || ''}
          availableVariables={messages.map(m => m.variable).filter(Boolean)}
        />
      )}

      {activeModal === 'userResponseType' && (
        <UserResponseTypeModal
          isOpen={true}
          onClose={closeModal}
          onSave={(data) => {
            console.log('UserResponseTypeModal data received:', data);
            
            if (data && data.type) {
              let formattedResponseType = data.type;
              
              if (data.config && Object.keys(data.config).length > 0) {
                const configPairs = Object.entries(data.config)
                  .map(([key, value]) => `${key}:${value}`)
                  .join(',');
                formattedResponseType = `${data.type}(${configPairs})`;
              }
              
              console.log('Formatted response type:', formattedResponseType);
              
              // Try direct state update instead of updateMessage
              const updatedMessages = [...messages];
              updatedMessages[editingMessageIndex] = {
                ...updatedMessages[editingMessageIndex],
                userResponseType: formattedResponseType
              };
              
              if (data.variable) {
                updatedMessages[editingMessageIndex].variable = data.variable;
              }
              
              console.log('Direct update - new messages array:', updatedMessages);
              onMessagesChange(updatedMessages);
            }
            
            closeModal();
          }}
          currentType={messages[editingMessageIndex]?.userResponseType || ''}
          currentVariable={messages[editingMessageIndex]?.variable || ''}
        />
      )}

      {activeModal === 'action' && (
        <ActionModal
          isOpen={true}
          onClose={closeModal}
          onSave={handleModalSave}
          currentValue={messages[editingMessageIndex]?.action || ''}
          availableMessages={messages}
        />
      )}

      {activeModal === 'integration' && (
        <IntegrationModal
          isOpen={true}
          onClose={closeModal}
          onSave={handleModalSave}
          currentValue={messages[editingMessageIndex]?.integrationParameter || ''}
          integrationType={messages[editingMessageIndex]?.integrationType || ''}
        />
      )}

      {activeModal === 'media' && (
        <MediaModal
          isOpen={true}
          onClose={closeModal}
          onSave={handleMediaSave}
          currentValue={messages[editingMessageIndex]?.mediaUrl || ''}
          bookId={book?.id}
        />
      )}

      {activeModal === 'messageConfig' && editingMessageIndex !== null && (
        <MessageConfigModal
          isOpen={true}
          onClose={closeModal}
          message={messages[editingMessageIndex]}
          bookCategory={book?.category || 'education'}
          onSave={handleMessageConfigSave}
        />
      )}

      {/* Enhanced Styles */}
      <style jsx>{`
        .math-field-input {
          font-family: 'Latin Modern Math', 'STIX Two Math', serif;
        }
        
        .markdown-content {
          line-height: 1.6;
        }
        
        .markdown-content h1,
        .markdown-content h2,
        .markdown-content h3 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        
        .markdown-content h1 { font-size: 1.5em; }
        .markdown-content h2 { font-size: 1.3em; }
        .markdown-content h3 { font-size: 1.1em; }
        
        .markdown-content p {
          margin: 0.5em 0;
        }
        
        .markdown-content strong {
          font-weight: 600;
        }
        
        .markdown-content em {
          font-style: italic;
        }
        
        .markdown-content code {
          background-color: #f3f4f6;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.875em;
        }
        
        .markdown-content mjx-container {
          display: inline-block;
          font-weight: normal !important;
        }

        .drag-active {
          cursor: grab;
        }

        .drag-active:active {
          cursor: grabbing;
        }

        .drag-preview {
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

// Enhanced Message Card Component
const MessageCard = ({ 
  message, 
  index, 
  onUpdate, 
  onDelete, 
  onDuplicate,
  onTogglePreview,
  previewMode,
  hasComplexContent,
  mathInitialized,
  openModal,
  validationErrors,
  dragEnabled,
  dragHandlers,
  onOpenModal,  
  bookCategory,
}) => {
  const [expanded, setExpanded] = useState(false);
  const textareaRefs = useRef({});
  const hasConfiguration = message.metadata || message.scoring || message.assessment;
  const isConfigured = hasConfiguration && (
    (message.metadata?.topics?.length > 0) ||
    (message.scoring?.enabled) ||
    (message.assessment?.isGraded)
  );

  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 600);
      textarea.style.height = `${newHeight}px`;
    }
  };

  const getMediaIcon = (mediaUrl) => {
    if (!mediaUrl) return null;
    
    const url = mediaUrl.toLowerCase();
    if (url.includes('image') || url.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      return <Image className="h-4 w-4" />;
    }
    if (url.includes('video') || url.match(/\.(mp4|avi|mov|wmv)$/)) {
      return <Video className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  return (
    <div 
      className={`bg-blue-50 border rounded-xl shadow-sm transition-all duration-200 ${
        dragHandlers.isDragged ? 'opacity-50 rotate-1 scale-105' : ''
      } ${
        dragHandlers.isDragOver ? 'border-blue-400 bg-blue-50 shadow-lg' : ''
      } ${
        validationErrors ? 'border-red-300 bg-red-50' : 'border-gray-200'
      } ${
        dragEnabled ? 'drag-active' : ''
      }`}
      draggable={dragEnabled}
      onDragStart={dragHandlers.onDragStart}
      onDragOver={dragHandlers.onDragOver}
      onDragEnd={dragHandlers.onDragEnd}
      onDrop={dragHandlers.onDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {/* Drag Handle - Always Visible */}
          <div 
            className={`flex-shrink-0 cursor-move p-2 rounded-lg transition-colors ${
              dragEnabled 
                ? 'text-blue-500 bg-blue-50 hover:bg-blue-100' 
                : 'text-gray-300 bg-gray-50'
            }`}
            onMouseDown={(e) => {
              if (dragEnabled) {
                // Don't call handleDragStart here, just ensure the parent is draggable
                e.currentTarget.closest('[draggable]').draggable = true;
              }
            }}
            title={dragEnabled ? 'Drag to reorder' : 'Enable drag mode to reorder'}
          >
            <GripVertical className="h-4 w-4" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">#{index + 1}</span>
            
            <select
              value={message.type || 'text'}
              onChange={(e) => onUpdate(index, 'type', e.target.value)}
              className="text-xs bg-gray-100 border-0 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()} // Prevent drag when interacting
            >
              <option value="text">Text</option>
              <option value="math">Math</option>
              <option value="form">Form</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
              <option value="embed">Embed</option>
            </select>

            {message.type === 'math' && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                <Calculator className="h-3 w-3" />
                Math
              </span>
            )}

            {message.mediaUrl && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                {getMediaIcon(message.mediaUrl)}
                Media
              </span>
            )}

            {validationErrors && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                <AlertCircle className="h-3 w-3" />
                Errors
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={expanded ? "Collapse" : "Expand"}
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(index);
            }}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>

          <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenModal('messageConfig', index);
                }}
                className={`p-2 rounded transition-colors flex items-center gap-1 ${
                  isConfigured 
                    ? 'text-green-600 bg-green-100 hover:bg-green-200' 
                    : 'text-gray-400 hover:text-blue-600 hover:bg-blue-100'
                }`}
                title={isConfigured ? 'Message configured - click to edit' : 'Configure message metadata and scoring'}
              >
                {isConfigured ? (
                  <>
                    <Award className="h-4 w-4" />
                    <span className="text-xs font-medium">Config</span>
                  </>
                ) : (
                  <Settings className="h-4 w-4" />
                )}
              </button>
              
              {/* Configuration status indicator */}
              {isConfigured && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="h-2 w-2 text-white" />
                </div>
              )}
            </div>
        </div>
      </div>

       {isConfigured && (
          <div className="mb-3 p-2 bg-green-50 rounded border border-green-200">
            <div className="flex items-center gap-2 text-sm text-green-800">
              <Award className="h-3 w-3" />
              <span className="font-medium">Configured:</span>
              <div className="flex items-center gap-3 text-xs">
                {message.metadata?.topics?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    {message.metadata.topics.length} topics
                  </span>
                )}
                {message.scoring?.enabled && (
                  <span className="flex items-center gap-1">
                    <Target className="h-3 w-3" />
                    {message.scoring.maxPoints}pts
                  </span>
                )}
                {message.assessment?.isGraded && (
                  <span className="flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Graded
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Content - Always Visible Preview + Expandable Details */}
      <div className="p-4">
        {/* Quick Preview */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <input
              value={message.section || ''}
              onChange={(e) => onUpdate(index, 'section', e.target.value)}
              className={`text-lg font-semibold bg-transparent border-0 focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 flex-1 ${
                validationErrors?.section ? 'text-red-600' : 'text-gray-900'
              }`}
              placeholder="Section title..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="text-sm text-gray-600 line-clamp-2">
            {message.message?.replace(/[#*`]/g, '').substring(0, 120)}
            {message.message?.length > 120 ? '...' : ''}
          </div>
        </div>

        {/* Expandable Details */}
        {expanded && (
          <div className="space-y-4 border-t pt-4">
            {/* Message Content */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-md font-medium text-gray-700">Message Content</label>
                {hasComplexContent && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePreview();
                    }}
                    className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    {previewMode ? <Code className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {previewMode ? 'Edit' : 'Preview'}
                  </button>
                )}
              </div>

              {/* Math Field for Math Type (Edit Mode) */}
              {message.type === 'math' && mathInitialized && !previewMode && (
                <MathField
                  value={message.message || ''}
                  onChange={(value) => onUpdate(index, 'message', value)}
                  placeholder="Enter LaTeX math expression..."
                />
              )}

              {/* Regular Textarea (Edit Mode) */}
              {message.type !== 'math' && !previewMode && (
              <textarea
                ref={(el) => {
                  if (el) {
                    textareaRefs.current[index] = el;
                    // Auto-resize on initial render
                    setTimeout(() => autoResizeTextarea(el), 0);
                  }
                }}
                value={message.message || ''}
                onChange={(e) => {
                  onUpdate(index, 'message', e.target.value);
                  // Auto-resize on content change
                  setTimeout(() => autoResizeTextarea(e.target), 0);
                }}
                className={`w-full text-md border rounded-lg px-3 py-2 min-h-[200px] resize-none overflow-y-auto focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors?.message ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Enter your message content..."
                onClick={(e) => e.stopPropagation()}
                style={{ height: 'auto' }}
              />
            )}

              {/* Preview Mode */}
              {previewMode && (
                <div className="text-sm border rounded-lg p-3 bg-gray-50 min-h-48">
                  {message.type === 'math' && mathInitialized ? (
                    <MathRenderer content={message.message || ''} />
                  ) : (
                    <div 
                      className="markdown-content"
                      dangerouslySetInnerHTML={{
                        __html: message.message || 'No content'
                      }}
                    />
                  )}
                </div>
              )}

              {validationErrors?.message && (
                <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.message}
                </div>
              )}
            </div>

            {/* Media Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Media</label>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal('media', index, 'mediaUrl');
                  }}
                  className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  <Upload className="h-3 w-3" />
                  {message.mediaUrl ? 'Edit' : 'Add'} Media
                </button>
              </div>
              
              {message.mediaUrl && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-sm">
                  {getMediaIcon(message.mediaUrl)}
                  <span className="text-gray-600 truncate flex-1">Media attached</span>
                  <a 
                    href={message.mediaUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Eye className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>

            {/* Response Configuration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Response Type</label>
                <div className="flex items-center gap-2">
                  <input
                    value={message.userResponseType || ''}
                    onChange={(e) => onUpdate(index, 'userResponseType', e.target.value)}
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Response type..."
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal('userResponseType', index, 'userResponseType');
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
                    title="Configure response type"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Variable</label>
                <input
                  value={message.variable || ''}
                  onChange={(e) => onUpdate(index, 'variable', e.target.value)}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors?.variable ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Variable name..."
                  onClick={(e) => e.stopPropagation()}
                />
                {validationErrors?.variable && (
                  <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.variable}
                  </div>
                )}
              </div>
            </div>

            {/* Advanced Configuration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                <div className="flex items-center gap-2">
                  <input
                    value={message.condition || ''}
                    onChange={(e) => onUpdate(index, 'condition', e.target.value)}
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Condition..."
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal('condition', index, 'condition');
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
                    title="Edit condition"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Integration</label>
                <div className="flex items-center gap-2">
                  <select
                    value={message.integrationType || ''}
                    onChange={(e) => onUpdate(index, 'integrationType', e.target.value)}
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">None</option>
                    <option value="Google Sheets">Google Sheets</option>
                    <option value="API">API</option>
                    <option value="Database">Database</option>
                  </select>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal('integration', index, 'integrationParameter');
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
                    title="Configure integration"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </div>
                {validationErrors?.integrationParameter && (
                  <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.integrationParameter}
                  </div>
                )}
              </div>
            </div>

            {/* Action Configuration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <div className="flex items-center gap-2">
                <input
                  value={message.action || ''}
                  onChange={(e) => onUpdate(index, 'action', e.target.value)}
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Action..."
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal('action', index, 'action');
                  }}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded"
                  title="Configure action"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableBookEditor;